// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Edit, Trash2, Calendar } from "lucide-react";
import PaginationComponent from "@/components/common/PaginationComponent";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSports } from "@/hooks/useSports";
import type { NcaaCalendarEvent } from "./AdminNcaaCalendarModule";

interface AdminNcaaCalendarTableProps {
  events: NcaaCalendarEvent[];
  loading: boolean;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onEdit: (event: NcaaCalendarEvent) => void;
  onEventDeleted: () => void;
  categoryFilter: string;
  onCategoryFilterChange: (category: string) => void;
  sourceFilter: string;
  onSourceFilterChange: (source: string) => void;
  sportFilter: string;
  onSportFilterChange: (sport: string) => void;
  dateRangeFilter: {start?: string, end?: string};
  onDateRangeFilterChange: (filter: {start?: string, end?: string}) => void;
}

const NCAA_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'ncaa_contact', label: 'NCAA Contact Period' },
  { value: 'ncaa_dead', label: 'NCAA Dead Period' },
  { value: 'ncaa_evaluation', label: 'NCAA Evaluation Period' },
  { value: 'ncaa_quiet', label: 'NCAA Quiet Period' },
  { value: 'fafsa', label: 'FAFSA Deadline' },
  { value: 'testing', label: 'Testing Date' },
  { value: 'other', label: 'Other Event' },
];

const EVENT_SOURCES = [
  { value: 'all', label: 'All Sources' },
  { value: 'ncaa', label: 'NCAA' },
  { value: 'fafsa', label: 'FAFSA' },
  { value: 'testing', label: 'Testing' },
  { value: 'custom', label: 'Custom' },
];

const AdminNcaaCalendarTable: React.FC<AdminNcaaCalendarTableProps> = ({
  events,
  loading,
  totalPages,
  currentPage,
  onPageChange,
  onEdit,
  onEventDeleted,
  categoryFilter,
  onCategoryFilterChange,
  sourceFilter,
  onSourceFilterChange,
  sportFilter,
  onSportFilterChange,
  dateRangeFilter,
  onDateRangeFilterChange,
}) => {
  const { toast } = useToast();
  const { sports } = useSports();
  const [sportsLookup, setSportsLookup] = useState<Record<string, any>>({});

  // Create sports lookup for quick access
  useEffect(() => {
    const lookup = {};
    sports.forEach(sport => {
      lookup[sport.id] = sport;
    });
    setSportsLookup(lookup);
  }, [sports]);

  const handleDelete = async (id: string, title: string) => {
    try {
      console.log("🏈 Calendar Admin: Deleting event:", { id, title });
      
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error("🏈 Calendar Admin: Delete error:", error);
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'Event deleted successfully'
      });
      
      onEventDeleted();
      
    } catch (error) {
      console.error('🏈 Calendar Admin: Error deleting event:', error);
      toast({
        title: 'Error',
        description: `Failed to delete event: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    }
  };

  const getCategoryBadge = (category: string) => {
    const categoryMap = {
      ncaa_contact: { label: 'Contact', color: 'bg-green-100 text-green-800' },
      ncaa_dead: { label: 'Dead', color: 'bg-red-100 text-red-800' },
      ncaa_evaluation: { label: 'Evaluation', color: 'bg-blue-100 text-blue-800' },
      ncaa_quiet: { label: 'Quiet', color: 'bg-yellow-100 text-yellow-800' },
      fafsa: { label: 'FAFSA', color: 'bg-purple-100 text-purple-800' },
      testing: { label: 'Testing', color: 'bg-orange-100 text-orange-800' },
      other: { label: 'Other', color: 'bg-gray-100 text-gray-800' },
    };
    
    const config = categoryMap[category] || { label: category, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getSourceBadge = (source: string | null) => {
    if (!source) return <Badge variant="outline">Unknown</Badge>;
    
    const sourceMap = {
      ncaa: { label: 'NCAA', color: 'bg-blue-100 text-blue-800' },
      fafsa: { label: 'FAFSA', color: 'bg-purple-100 text-purple-800' },
      testing: { label: 'Testing', color: 'bg-orange-100 text-orange-800' },
      custom: { label: 'Custom', color: 'bg-gray-100 text-gray-800' },
    };
    
    const config = sourceMap[source] || { label: source, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateRange = (startDate: string, endDate?: string | null) => {
    const start = formatDate(startDate);
    if (!endDate || endDate === startDate) return start;
    const end = formatDate(endDate);
    return `${start} - ${end}`;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-end flex-wrap">
        <div className="min-w-[200px]">
          <Label htmlFor="category-filter">Category Filter</Label>
          <Select value={categoryFilter} onValueChange={onCategoryFilterChange}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              {NCAA_CATEGORIES.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[200px]">
          <Label htmlFor="source-filter">Source Filter</Label>
          <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by source" />
            </SelectTrigger>
            <SelectContent>
              {EVENT_SOURCES.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[200px]">
          <Label htmlFor="sport-filter">Sport Filter</Label>
          <Select value={sportFilter} onValueChange={onSportFilterChange}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by sport" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              <SelectItem value="none">No Sport Specified</SelectItem>
              {sports.map(sport => (
                <SelectItem key={sport.id} value={sport.id}>
                  {sport.name}{sport.gender ? ` - ${sport.gender}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="min-w-[150px]">
          <Label htmlFor="start-date-filter">Start Date</Label>
          <Input
            id="start-date-filter"
            type="date"
            value={dateRangeFilter.start || ""}
            onChange={(e) => onDateRangeFilterChange({
              ...dateRangeFilter,
              start: e.target.value || undefined
            })}
          />
        </div>
        
        <div className="min-w-[150px]">
          <Label htmlFor="end-date-filter">End Date</Label>
          <Input
            id="end-date-filter"
            type="date"
            value={dateRangeFilter.end || ""}
            onChange={(e) => onDateRangeFilterChange({
              ...dateRangeFilter,
              end: e.target.value || undefined
            })}
          />
        </div>
        
        {(dateRangeFilter.start || dateRangeFilter.end || categoryFilter !== 'all' || sourceFilter !== 'all' || sportFilter !== 'all') && (
          <Button 
            variant="outline" 
            onClick={() => {
              onDateRangeFilterChange({});
              onCategoryFilterChange('all');
              onSourceFilterChange('all');
              onSportFilterChange('all');
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Events Table */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading calendar events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-8 border rounded-md">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {categoryFilter !== 'all' || sourceFilter !== 'all' || sportFilter !== 'all' || dateRangeFilter.start || dateRangeFilter.end ? 
              'No calendar events found matching your filters' : 
              'No calendar events found. Add your first event!'
            }
          </p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event Title</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Sport</TableHead>
                <TableHead>Division</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">
                    {event.title}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      {formatDateRange(event.start_date, event.end_date)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {getCategoryBadge(event.category)}
                  </TableCell>
                  <TableCell>
                    {getSourceBadge(event.source)}
                  </TableCell>
                  <TableCell>
                    {(event as any).sport_id && sportsLookup[(event as any).sport_id] ? (
                      <Badge variant="outline">
                        {sportsLookup[(event as any).sport_id].name}
                        {sportsLookup[(event as any).sport_id].gender ? ` - ${sportsLookup[(event as any).sport_id].gender}` : ''}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {event.division || '-'}
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {event.description || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onEdit(event)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Calendar Event</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{event.title}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(event.id, event.title)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminNcaaCalendarTable;
